import { useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { animateScroll as scroll } from 'react-scroll';

import ContentNav from '@/components/schoolsResources/ContentNav';
import ContentPlayer from '@/components/schoolsResources/ContentPlayer';
import { classRoomUrl } from '@/components/schoolsResources/Guides/data';
import { useAnalytics } from '@/hooks/analytics';
import { useLocale } from '@/hooks/locale/useLocale';
import { useElementDimensions } from '@/hooks/ui/useElementDimensions';

import { SupportedSchoolLanguage } from '../Guides/types';
import { teacherContent } from './data';
import messages from './messages';
import { BodyText, ContentHeading, Grid, Heading, PDF, PlayerContainer, Wrapper } from './styles';

const CuratedContent = () => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const searchParams = useSearchParams();
	const { calmLanguage } = useLocale();
	const [currentDay, setCurrentDay] = useState(1);
	const [playerEl, playerDimensions] = useElementDimensions();
	const wrapperEl = useRef<HTMLDivElement>(null);

	const currentPack = teacherContent.filter(content => calmLanguage === 'en' || !content.isEnglishOnly);
	const totalDays = currentPack.length;
	const currentContent = currentPack[currentDay - 1];
	const currentDayLocale = currentContent[calmLanguage] || currentContent.en;
	const currentDayTitle = currentDayLocale?.title;
	const currentDayContent = currentDayLocale?.content;

	useEffect(
		() => {
			const FIRST_DAY = 1;
			const LAST_DAY = totalDays;
			const queryDay = searchParams?.get('day');

			if (!queryDay || +queryDay > LAST_DAY || +queryDay < FIRST_DAY) {
				logEvent({
					eventName: 'Schools : Resources : Content : Landed',
					eventProps: {
						day: 1,
					},
				});
				return;
			}

			logEvent({
				eventName: 'Schools : Resources : Content : Landed',
				eventProps: {
					day: queryDay,
				},
			});
			setCurrentDay(+queryDay);

			if (wrapperEl?.current) {
				scroll.scrollTo(wrapperEl?.current?.offsetTop, {
					smooth: 'easeInOutCubic',
					delay: 100,
					duration: 1000,
				});
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[searchParams],
	);

	const onPageChange = (nextDay: number) => {
		setCurrentDay(nextDay);

		logEvent({
			eventName: 'Schools : Resources : Content : Changed',
			eventProps: {
				day: nextDay,
			},
		});
	};

	const onCtaClick = (
		copy: string,
		href = classRoomUrl[calmLanguage as SupportedSchoolLanguage] || classRoomUrl.en,
	) => {
		logEvent({
			eventName: 'Schools : Resources : CTA : Clicked',
			eventProps: {
				copy,
				href,
				type: 'link',
			},
		});
	};

	return (
		<Wrapper ref={wrapperEl}>
			<Grid>
				<div>
					<Heading>{formatMessage(messages.heading)}</Heading>
					<BodyText>
						{formatMessage(messages.bodyText, {
							underlinedtext: (chunks: ReactNode) => (
								<a
									href={classRoomUrl[calmLanguage as SupportedSchoolLanguage] || classRoomUrl.en}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => onCtaClick("Calm's 30 Days of Mindfulness in the Classroom")}
								>
									{chunks}
								</a>
							),
						})}
					</BodyText>
					<ContentNav currentDay={currentDay} totalDays={totalDays} onPageChange={onPageChange} />
				</div>
				<div ref={playerEl}>
					<ContentHeading>
						{formatMessage(messages.day)}
						{currentDay} - {formatMessage(currentDayTitle)}
					</ContentHeading>
					{currentDayContent.length > 0 ? (
						<PlayerContainer isMultiple={!!currentDayLocale.isMultiple}>
							{currentDayContent.map(dayContent => (
								<ContentPlayer
									key={formatMessage(dayContent.contentTitle)}
									contentTitle={dayContent.contentTitle}
									contentAuthor={dayContent.contentAuthor}
									thumbnail={dayContent.thumbnail}
									contentUrl={dayContent.contentUrl}
									type={dayContent.type}
									loop={dayContent.loop}
									playerWidth={playerDimensions.width}
									isMultiple={currentDayLocale.isMultiple}
								/>
							))}
						</PlayerContainer>
					) : (
						<PDF>{formatMessage(messages.referToPdf)}</PDF>
					)}
				</div>
			</Grid>
		</Wrapper>
	);
};

export default CuratedContent;
